import {useQuery} from '@tanstack/react-query'

import {APP_API_URL} from '#/lib/constants'
import {useDebouncedValue} from '#/lib/hooks/useDebouncedValue'
import {createFullHandle} from '#/lib/strings/handles'
import {logger} from '#/logger'
import {useAnalytics} from '#/analytics'

export const RQKEY_handleAvailability = (
  handle: string,
  domain: string,
  serviceDid: string,
) => ['handle-availability', {handle, domain, serviceDid}]

export function useHandleAvailabilityQuery(
  {
    username,
    serviceDomain,
    serviceDid,
    enabled,
    birthDate,
    email,
  }: {
    username: string
    serviceDomain: string
    serviceDid: string
    enabled: boolean
    birthDate?: string
    email?: string
  },
  debounceDelayMs = 500,
) {
  const ax = useAnalytics()
  const name = username.trim()
  const debouncedHandle = useDebouncedValue(name, debounceDelayMs)

  return {
    debouncedUsername: debouncedHandle,
    enabled: enabled && name === debouncedHandle,
    query: useQuery({
      enabled: enabled && name === debouncedHandle,
      queryKey: RQKEY_handleAvailability(
        debouncedHandle,
        serviceDomain,
        serviceDid,
      ),
      queryFn: async () => {
        const handle = createFullHandle(name, serviceDomain)
        const res = await checkHandleAvailability(handle, serviceDid, {
          email,
          birthDate,
        })
        if (res.available) {
          ax.metric('signup:handleAvailable', {typeahead: true})
        } else {
          ax.metric('signup:handleTaken', {typeahead: true})
        }
        return res
      },
    }),
  }
}

export async function checkHandleAvailability(
  handle: string,
  _serviceDid?: string,
  options?: {email?: string; birthDate?: string; typeahead?: boolean},
) {
  const typeahead = options?.typeahead
  const url = `${APP_API_URL}/xrpc/eu.wsocial.server.checkHandleAvailability?handle=${encodeURIComponent(handle)}`
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`checkHandleAvailability failed: ${res.statusText}`)
  }
  const data = (await res.json()) as {available: boolean}
  if (data.available) {
    logger.metric('signup:handleAvailable', {typeahead}, {statsig: true})
    return {available: true} as const
  } else {
    logger.metric('signup:handleTaken', {typeahead}, {statsig: true})
    return {available: false} as const
  }
}
